import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import classNames from 'classnames';

import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import MessagePropType from '../definitions/MessagePropType';
import { BOX_SHADOW_BLUEBERRY } from '../definitions/SharedInputStyles';
import RemoveDefaultButtonStyles from '../styles/RemoveDefaultButtonStyles';
import ACCORDION_HEADER_SIZES from '../definitions/AccordionHeaderSizes';
import ACCORDION_HEADER_WEIGHTS from '../definitions/AccordionHeaderWeights';
import IconMinus from './icons/IconMinus';
import IconPlus from './icons/IconPlus';
import IconGarbage from './icons/IconGarbage';


const PADDING_CLASSES = {
    [ACCORDION_HEADER_SIZES.LARGE]: 'rh-p-1',
    [ACCORDION_HEADER_SIZES.MEDIUM]: 'rh-p-0_875',
    [ACCORDION_HEADER_SIZES.SMALL]: 'rh-p-0_75',
};
const FONT_CLASSES = {
    [ACCORDION_HEADER_SIZES.LARGE]: 'rh-title-m',
    [ACCORDION_HEADER_SIZES.MEDIUM]: 'rh-title-s',
    [ACCORDION_HEADER_SIZES.SMALL]: 'rh-title-xs',
};

function AccordionBlueberryHeader({
    isExpanded = false,
    onClick,
    onDelete,
    title,
    size = ACCORDION_HEADER_SIZES.LARGE,
    weight = ACCORDION_HEADER_WEIGHTS.REGULAR,
    backgroundColour = Colours.BLUEBERRY_LIGHTEST,
}) {
    const intl = useIntl();

    const paddingClass = PADDING_CLASSES[size];
    const fontClass = FONT_CLASSES[size];

    return (
        <Container
            className={classNames(
                'accordion-fieldset-header',
                paddingClass,
            )}
            $backgroundColour={backgroundColour}
        >
            <div
                className="header-button"
                role="button"
                tabIndex="0"
                onClick={onClick}
                /* Accordions should be toggled with either Enter or Space */
                /* @TODO this should be moved to AccordionBase or AccordionBasicItem
                so other variants can enjoy this same basic level of accessibility.
                The default variant I don't even think you can focus, let alone
                open/close with the keyboard :P */
                onKeyDown={(evt) => (evt?.key === 'Enter' || evt?.key === ' ') && onClick(evt)}
                data-name="toggle-accordion"

                aria-label={intl.formatMessage(isExpanded
                    ? MESSAGES.COLLAPSE
                    : MESSAGES.EXPAND)}
            >
                <Choose>
                    <When condition={isExpanded}>
                        <IconMinus
                            className="expand-icon rh-icon-m"
                            outlineWidth="0"
                            stroke={Colours.BLUEBERRY}
                        />
                    </When>
                    <Otherwise>
                        <IconPlus
                            className="expand-icon rh-icon-m"
                            outlineWidth="0"
                            stroke={Colours.BLUEBERRY}
                        />
                    </Otherwise>
                </Choose>


                <h3 className={classNames(
                    'title rh-my-0 rh-mr-0 rh-ml-1',
                    fontClass,
                    {
                        'weight-regular': weight === ACCORDION_HEADER_WEIGHTS.REGULAR,
                        'weight-medium': weight === ACCORDION_HEADER_WEIGHTS.MEDIUM,
                    })}
                >
                    {title}
                </h3>
            </div>

            <If condition={onDelete}>
                <button
                    className="delete-button"
                    type="button"
                    onClick={onDelete}
                    data-name="delete-accordion"

                    aria-label={intl.formatMessage(MESSAGES.DELETE)}
                >
                    <IconGarbage
                        className="delete-icon rh-icon-xs"
                        outlineWidth="0"
                        stroke={Colours.BLUEBERRY_DARK}
                    />
                </button>
            </If>
        </Container>
    );
}

AccordionBlueberryHeader.propTypes = {
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,
    onDelete: PropTypes.func,
    title: MessagePropType.isRequired,
    size: PropTypes.oneOf(Object.values(ACCORDION_HEADER_SIZES)),
    weight: PropTypes.oneOf(Object.values(ACCORDION_HEADER_WEIGHTS)),
    backgroundColour: PropTypes.oneOf(Object.values([ Colours.TRANSPARENT, Colours.BLUEBERRY_LIGHTEST ])),
};

const Container = styled.div`
    display: flex;
    flex-wrap: nowrap;

    overflow: hidden;
    width: 100%;

    background-color: ${props => props.$backgroundColour};

    border-left: 2px solid ${Colours.BLUEBERRY};
    box-sizing: border-box;

    > .header-button {
        flex-grow: 1;

        display: flex;
        flex-wrap: nowrap;
        align-items: center;

        overflow: hidden;
        width: 100%;

        ${RemoveDefaultButtonStyles};
        cursor: pointer;

        > .expand-icon {
            flex: ${Sizes.ICONS.M} 0 0;
        }

        > .title {
            flex-grow: 1;

            color: ${Colours.BLUEBERRY_DARK};

            transition: color 300ms;
        }

        &:hover {
            > .title {
                color: ${Colours.BLUEBERRY};
            }
        }

        &:active {
            > .expand-icon {
                stroke: ${Colours.BLUEBERRY_DARKEST};
            }

            > .title {
                color: ${Colours.BLUEBERRY_DARKEST};
            }
        }
    }

    > .delete-button {
        flex: ${Sizes.ICONS.M} 0 0;
        margin-left: ${Sizes.SPACING.ONE};

        ${RemoveDefaultButtonStyles};
        cursor: pointer;

        &:hover,
        &:focus {
            > .delete-icon {
                stroke: ${Colours.BLUEBERRY};
            }
        }

        &:active {
            > .delete-icon {
                stroke: ${Colours.BLUEBERRY_DARKEST};
            }
        }
    }

    &:focus-within {
        box-shadow: ${BOX_SHADOW_BLUEBERRY};
    }
`;


const MESSAGES = defineMessages({
    EXPAND: {
        id: 'base-ui.AccordionFieldset.expand',
        defaultMessage: 'Expand accordion',
    },
    COLLAPSE: {
        id: 'base-ui.AccordionFieldset.collapse',
        defaultMessage: 'Collapse accordion',
    },
    DELETE: {
        id: 'base-ui.AccordionFieldset.delete',
        defaultMessage: 'Delete accordion',
    },
    DELETE_SCREEN_READER: {
        id: 'base-ui.AccordionFieldset.deleteScreenReader',
        defaultMessage: 'Delete confirmation modal',
    },
});

export default AccordionBlueberryHeader;
